import { create } from 'zustand';
import type { User } from '@home-guard/shared';
import { API_BASE } from './apiBase';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  initialized: boolean;
  setAuth: (user: User, token: string, expiresIn: number) => void;
  setToken: (token: string, expiresIn: number) => void;
  logout: () => void;
  setInitialized: () => void;
}

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresIn: number): void {
  if (_refreshTimer) clearTimeout(_refreshTimer);
  // Refresh 60 seconds before expiry to avoid hitting expired tokens
  const delay = Math.max((expiresIn - 60) * 1000, 0);
  _refreshTimer = setTimeout(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json() as AuthResponse;
        useAuthStore.getState().setAuth(data.user, data.access_token, data.expires_in);
      } else {
        useAuthStore.getState().logout();
      }
    } catch {
      // Network error on background refresh — don't force logout
    }
  }, delay);
}

export interface AuthResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  user: User;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  accessToken: null,
  initialized: false,
  setAuth: (user, token, expiresIn) => {
    scheduleRefresh(expiresIn);
    set({ user, accessToken: token });
  },
  setToken: (token, expiresIn) => {
    scheduleRefresh(expiresIn);
    set({ accessToken: token });
  },
  logout: () => {
    if (_refreshTimer) clearTimeout(_refreshTimer);
    _refreshTimer = null;
    set({ user: null, accessToken: null });
  },
  setInitialized: () => set({ initialized: true }),
}));
